import React from "react";

import type { ProjectMember } from "../../../../../hooks/useProjectSharing";
import {
  type ProjectViewLinkInvitation,
  type ProjectViewLinkInvitationID,
  useMutateUpdateProjectViewLinkInvitation,
} from "../../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../../hooks/useTracker";
import {
  useWebExport,
  WebExportAssignableUserRoles,
  WebExportAssignableUserRolesDisplayName,
} from "../../../../../hooks/useWebExport";
import { useShareBadge } from "./ShareBadge";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface InvitationRowProps {
  invitation: ProjectViewLinkInvitation;
  projectUser?: ProjectMember;
  onRemove?: (invitationId: ProjectViewLinkInvitationID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  tooltipText?: string;
}

export const InvitationRowViewLink = React.forwardRef<ShareRowRef, InvitationRowProps>(
  ({ invitation, onRemove, containerIntersectionObserver, tooltipText, projectUser }, ref) => {
    const tracker = useTracker();
    const shareBadge = useShareBadge();
    const { data: webExportData, viewLinkId } = useWebExport();

    const {
      mutate: mutateUpdateViewLinkInvitation,
      isPending: isUpdatePending,
      isError: isUpdateError,
    } = useMutateUpdateProjectViewLinkInvitation({
      invitationId: invitation.invitationId,
    });

    return (
      <ShareRow
        ref={ref}
        roles={WebExportAssignableUserRoles}
        roleDisplayName={WebExportAssignableUserRolesDisplayName}
        entity={invitation}
        containerIntersectionObserver={containerIntersectionObserver}
        onRemove={() => {
          onRemove?.(invitation.invitationId);
          tracker.trackShareModalMemberRemoveIntended({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            invitationId: invitation.invitationId,
            sharedResource: "review_link",
          });
        }}
        onUpdate={(prevRole, newRole) => {
          tracker.trackShareModalMemberRoleChanged({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            invitationId: invitation.invitationId,
            oldValue: prevRole,
            newValue: newRole,
            sharedResource: "review_link",
          });
          mutateUpdateViewLinkInvitation({
            role: newRole,
          });
        }}
        isPending={isUpdatePending}
        isError={isUpdateError}
        sharingBadges={[
          ...(projectUser?.isTeamUser === false ? [shareBadge("guest")] : []),
          shareBadge("invited"),
        ]}
        tooltipText={tooltipText}
      />
    );
  }
);
